import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./CreateEvaluation.css";
import {
  getClases,
  routeCreateEvaluation,
  routeGetEvaluations,
  routeUpdateEvaluation,
} from "./constants";

const tiposEvaluacion = [
  {
    key: "diagnostica",
    label: "Evaluación diagnóstica",
    description: "Formulario que se presenta antes de comenzar la clase.",
  },
  {
    key: "final",
    label: "Evaluación final",
    description: "Formulario para evaluar los conocimientos al terminar.",
  },
  {
    key: "satisfaccion",
    label: "Encuesta de satisfacción",
    description: "Formulario para conocer la experiencia del alumno.",
  },
];

const extraerUrlFormulario = (valor) => {
  const contenido = valor.trim();

  if (!contenido) {
    return "";
  }

  const coincidenciaSrc = contenido.match(/src\s*=\s*["']([^"']+)["']/i);
  return (coincidenciaSrc ? coincidenciaSrc[1] : contenido)
    .replace(/&amp;/g, "&")
    .trim();
};

const esUrlValida = (valor) => {
  try {
    const url = new URL(valor);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

const modalOverlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0, 0, 0, 0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
  zIndex: 1000,
};

const modalStyle = {
  backgroundColor: "#ffffff",
  borderRadius: "12px",
  boxShadow: "0 8px 30px rgba(0, 0, 0, 0.25)",
  maxWidth: "440px",
  padding: "28px",
  textAlign: "center",
  width: "100%",
};

export const CreateEvaluation = () => {
  const [matriculaClase, setMatriculaClase] = useState("");
  const [idClase, setIdClase] = useState("");
  const [evaluacionDiagnostica, setEvaluacionDiagnostica] = useState("");
  const [evaluacionFinal, setEvaluacionFinal] = useState("");
  const [satisfaccion, setSatisfaccion] = useState("");
  const [matriculas, setMatriculas] = useState([]);
  const [cargandoClases, setCargandoClases] = useState(true);
  const [cargandoEvaluaciones, setCargandoEvaluaciones] = useState(false);
  const [existeEvaluacion, setExisteEvaluacion] = useState(false);
  const [errores, setErrores] = useState({});
  const [estadoGuardado, setEstadoGuardado] = useState(null);
  const [mensajeGuardado, setMensajeGuardado] = useState("");
  const navigate = useNavigate();
  const estaGuardando = estadoGuardado === "guardando";

  const valoresEvaluacion = {
    diagnostica: evaluacionDiagnostica,
    final: evaluacionFinal,
    satisfaccion,
  };

  const settersEvaluacion = {
    diagnostica: setEvaluacionDiagnostica,
    final: setEvaluacionFinal,
    satisfaccion: setSatisfaccion,
  };

  useEffect(() => {
    const cargarClases = async () => {
      try {
        const response = await axios.get(getClases);
        setMatriculas(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error al obtener clases:", error);
        setMensajeGuardado("No fue posible cargar las clases.");
        setEstadoGuardado("error");
      } finally {
        setCargandoClases(false);
      }
    };

    cargarClases();
  }, []);

  const limpiarEvaluaciones = () => {
    setEvaluacionDiagnostica("");
    setEvaluacionFinal("");
    setSatisfaccion("");
    setExisteEvaluacion(false);
    setErrores({});
  };

  const fetchEvaluacionData = async (claseId) => {
    setCargandoEvaluaciones(true);
    limpiarEvaluaciones();

    try {
      const response = await axios.get(routeGetEvaluations, {
        params: { id_evaluaciones: claseId },
      });
      const evaluacion = response.data;
      const existe = Boolean(evaluacion && Object.keys(evaluacion).length > 0);

      setExisteEvaluacion(existe);
      if (existe) {
        setEvaluacionDiagnostica(evaluacion.evaluacion_diagnostica || "");
        setEvaluacionFinal(evaluacion.evaluacion_final || "");
        setSatisfaccion(evaluacion.satisfaccion || "");
      }
    } catch (error) {
      console.error("Error al obtener la evaluación:", error);
      setMensajeGuardado(
        "No fue posible consultar las evaluaciones de esta clase.",
      );
      setEstadoGuardado("error");
    } finally {
      setCargandoEvaluaciones(false);
    }
  };

  const handleSelection = (event) => {
    const valor = event.target.value;
    setMatriculaClase(valor);

    const claseSeleccionada = matriculas.find(
      (clase) => `${clase.matricula_clase} - ${clase.nombre_clase}` === valor,
    );

    if (!claseSeleccionada) {
      setIdClase("");
      limpiarEvaluaciones();
      return;
    }

    setIdClase(claseSeleccionada.id_clase);
    fetchEvaluacionData(claseSeleccionada.id_clase);
  };

  const handleEvaluationChange = (tipo, valor) => {
    settersEvaluacion[tipo](valor);
    setErrores((erroresActuales) => ({ ...erroresActuales, [tipo]: "" }));
  };

  const validarFormulario = () => {
    const nuevosErrores = {};

    if (!idClase) {
      nuevosErrores.clase = "Selecciona una clase válida de la lista.";
    }

    let evaluacionesConfiguradas = 0;

    tiposEvaluacion.forEach((tipo) => {
      const url = extraerUrlFormulario(valoresEvaluacion[tipo.key]);

      if (!url) {
        return;
      }

      evaluacionesConfiguradas += 1;
      if (!esUrlValida(url)) {
        nuevosErrores[tipo.key] =
          "Pega una URL válida o el código iframe completo.";
      }
    });

    if (idClase && evaluacionesConfiguradas === 0) {
      nuevosErrores.formularios =
        "Agrega al menos una evaluación antes de guardar.";
    }

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const handleSubmitEvaluation = async (event) => {
    event.preventDefault();

    if (!validarFormulario()) {
      return;
    }

    const payload = {
      id_evaluaciones: idClase,
      evaluacion_diagnostica: extraerUrlFormulario(evaluacionDiagnostica),
      evaluacion_final: extraerUrlFormulario(evaluacionFinal),
      satisfaccion: extraerUrlFormulario(satisfaccion),
    };

    setEstadoGuardado("guardando");
    setMensajeGuardado(
      existeEvaluacion
        ? "Actualizando las evaluaciones..."
        : "Creando las evaluaciones...",
    );

    try {
      const response = existeEvaluacion
        ? await axios.put(routeUpdateEvaluation, payload)
        : await axios.post(routeCreateEvaluation, payload);

      if (response.data?.error || response.data?.success === false) {
        throw new Error(response.data.error || response.data.message);
      }

      setEvaluacionDiagnostica(payload.evaluacion_diagnostica);
      setEvaluacionFinal(payload.evaluacion_final);
      setSatisfaccion(payload.satisfaccion);
      setExisteEvaluacion(true);
      setEstadoGuardado("exito");
      setMensajeGuardado(
        response.data?.mensaje ||
          response.data?.message ||
          "Las evaluaciones se guardaron correctamente.",
      );
    } catch (error) {
      console.error("Error al guardar las evaluaciones:", error);
      setEstadoGuardado("error");
      setMensajeGuardado(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "No fue posible guardar las evaluaciones.",
      );
    }
  };

  const handleCerrarResultado = () => {
    if (!estaGuardando) {
      setEstadoGuardado(null);
      setMensajeGuardado("");
    }
  };

  return (
    <div className="create-evaluation-page">
      <main className="create-evaluation-container">
        <header className="create-evaluation-header">
          <p className="create-evaluation-eyebrow">
            Configuración de formularios
          </p>
          <h1>Evaluaciones de clase</h1>
          <p>
            Selecciona una clase y agrega los formularios que verán los alumnos.
            Puedes pegar la URL o el código iframe completo de Google Forms.
          </p>
        </header>

        <form onSubmit={handleSubmitEvaluation} noValidate>
          <section className="create-evaluation-class">
            <label htmlFor="clase-evaluacion">Clase</label>
            <input
              id="clase-evaluacion"
              type="text"
              placeholder={
                cargandoClases
                  ? "Cargando clases..."
                  : "Busca por matrícula o nombre"
              }
              value={matriculaClase}
              onChange={handleSelection}
              list="clases-list"
              disabled={cargandoClases || estaGuardando}
              autoComplete="off"
            />
            <datalist id="clases-list">
              {matriculas.map((clase) => (
                <option
                  key={clase.id_clase}
                  value={`${clase.matricula_clase} - ${clase.nombre_clase}`}
                />
              ))}
            </datalist>
            {errores.clase && (
              <p className="create-evaluation-error">{errores.clase}</p>
            )}
            {idClase && (
              <p className="create-evaluation-status">
                {cargandoEvaluaciones
                  ? "Consultando evaluaciones..."
                  : existeEvaluacion
                    ? "Esta clase ya tiene evaluaciones. Puedes actualizarlas."
                    : "Esta clase todavía no tiene evaluaciones."}
              </p>
            )}
          </section>

          {errores.formularios && (
            <p className="create-evaluation-error">
              {errores.formularios}
            </p>
          )}

          <div className="create-evaluation-grid">
            {tiposEvaluacion.map((tipo, index) => {
              const valor = valoresEvaluacion[tipo.key];
              const urlPreview = extraerUrlFormulario(valor);
              const mostrarPreview = esUrlValida(urlPreview);

              return (
                <section className="create-evaluation-card" key={tipo.key}>
                  <div className="create-evaluation-card-heading">
                    <span>{index + 1}</span>
                    <div>
                      <h2>{tipo.label}</h2>
                      <p>{tipo.description}</p>
                    </div>
                  </div>

                  <label htmlFor={`evaluacion-${tipo.key}`}>
                    URL o código iframe (opcional)
                  </label>
                  <textarea
                    id={`evaluacion-${tipo.key}`}
                    rows="4"
                    placeholder='<iframe src="https://docs.google.com/forms/..."></iframe>'
                    value={valor}
                    onChange={(event) =>
                      handleEvaluationChange(tipo.key, event.target.value)
                    }
                    disabled={!idClase || cargandoEvaluaciones || estaGuardando}
                  />
                  {errores[tipo.key] && (
                    <p className="create-evaluation-error">
                      {errores[tipo.key]}
                    </p>
                  )}

                  <div className="create-evaluation-preview">
                    <h3>Vista previa</h3>
                    {mostrarPreview ? (
                      <iframe
                        src={urlPreview}
                        title={`Vista previa de ${tipo.label}`}
                        loading="lazy"
                      >
                        Cargando formulario...
                      </iframe>
                    ) : (
                      <p>
                        {valor.trim()
                          ? "Agrega un enlace válido para mostrar el formulario aquí."
                          : "Esta evaluación todavía no tiene un formulario configurado."}
                      </p>
                    )}
                  </div>
                </section>
              );
            })}
          </div>

          <div className="create-evaluation-actions">
            <button
              type="submit"
              disabled={!idClase || cargandoEvaluaciones || estaGuardando}
            >
              {estaGuardando
                ? "Guardando..."
                : existeEvaluacion
                  ? "Actualizar evaluaciones"
                  : "Crear evaluaciones"}
            </button>
            <button
              type="button"
              className="create-evaluation-secondary"
              onClick={() => navigate("/admin-home")}
              disabled={estaGuardando}
            >
              Regresar al inicio
            </button>
          </div>
        </form>
      </main>

      {estadoGuardado && (
        <div style={modalOverlayStyle} role="presentation">
          <div
            style={modalStyle}
            role="dialog"
            aria-modal="true"
            aria-labelledby="evaluation-status-title"
          >
            <h2 id="evaluation-status-title">
              {estadoGuardado === "guardando" && "Guardando evaluaciones"}
              {estadoGuardado === "exito" && "Evaluaciones guardadas"}
              {estadoGuardado === "error" && "No se pudo completar"}
            </h2>
            <p>{mensajeGuardado}</p>
            {estaGuardando ? (
              <div
                className="create-evaluation-loader"
                aria-label="Guardando"
              />
            ) : (
              <button type="button" onClick={handleCerrarResultado}>
                Cerrar
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
